import { supabase } from '../lib/supabase';
import { offlineQueue } from '../utils/offlineQueue';

export const orderService = {
  /**
   * Get user orders (projects)
   * @param {string} userId - User ID
   * @returns {Promise<Array>} - Array of orders
   */
  async getUserOrders(userId) {
    const { data, error } = await supabase?.from('orders')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      orderNumber: row?.order_number,
      projectName: row?.project_name,
      projectDescription: row?.project_description,
      projectData: row?.project_data,
      status: row?.status,
      paymentStatus: row?.payment_status,
      subtotal: row?.subtotal,
      taxAmount: row?.tax_amount,
      totalAmount: row?.total_amount,
      currency: row?.currency,
      paymentIntentId: row?.payment_intent_id,
      userId: row?.user_id,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    }));
  },

  /**
   * Get single order by ID
   * @param {string} orderId - Order ID
   * @returns {Promise<Object>} - Order object
   */
  async getOrderById(orderId) {
    const { data, error } = await supabase?.from('orders')?.select('*')?.eq('id', orderId)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      orderNumber: data?.order_number,
      projectName: data?.project_name,
      projectDescription: data?.project_description,
      projectData: data?.project_data,
      status: data?.status,
      paymentStatus: data?.payment_status,
      subtotal: data?.subtotal,
      taxAmount: data?.tax_amount,
      totalAmount: data?.total_amount,
      currency: data?.currency,
      paymentIntentId: data?.payment_intent_id,
      userId: data?.user_id,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Enhanced create order with offline support
   */
  async createOrder(orderData) {
    try {
      const { data, error } = await supabase?.from('orders')?.insert(orderData)?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      // Check if offline or network error
      if (!navigator.onLine || error?.message?.includes('fetch') || error?.message?.includes('network')) {
        // Queue for later sync
        const queueId = offlineQueue?.enqueue({
          method: 'insert',
          table: 'orders',
          data: orderData,
          type: 'create_order'
        });

        return {
          queued: true,
          queueId,
          message: 'Order creation queued. Will sync when online.'
        };
      }

      throw error;
    }
  },

  /**
   * Enhanced update order with offline support
   */
  async updateOrder(orderId, updates) {
    try {
      const { data, error } = await supabase?.from('orders')?.update(updates)?.eq('id', orderId)?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      // Check if offline or network error
      if (!navigator.onLine || error?.message?.includes('fetch') || error?.message?.includes('network')) {
        // Queue for later sync
        const queueId = offlineQueue?.enqueue({
          method: 'update',
          table: 'orders',
          data: updates,
          id: orderId,
          type: 'update_order'
        });

        return {
          queued: true,
          queueId,
          message: 'Order update queued. Will sync when online.'
        };
      }

      throw error;
    }
  },

  /**
   * Update order/project data
   * @param {string} orderId - Order ID
   * @param {Object} updates - Update data
   * @returns {Promise<Object>} - Updated order
   */
  async updateOrder(orderId, updates) {
    const dbUpdates = {};

    if (updates?.projectName !== undefined) dbUpdates.project_name = updates?.projectName;
    if (updates?.projectDescription !== undefined) dbUpdates.project_description = updates?.projectDescription;
    if (updates?.projectData !== undefined) dbUpdates.project_data = updates?.projectData;
    if (updates?.status !== undefined) dbUpdates.status = updates?.status;

    const { data, error } = await supabase?.from('orders')?.update(dbUpdates)?.eq('id', orderId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      orderNumber: data?.order_number,
      projectName: data?.project_name,
      projectDescription: data?.project_description,
      projectData: data?.project_data,
      status: data?.status,
      paymentStatus: data?.payment_status,
      subtotal: data?.subtotal,
      taxAmount: data?.tax_amount,
      totalAmount: data?.total_amount,
      currency: data?.currency,
      paymentIntentId: data?.payment_intent_id,
      userId: data?.user_id,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Subscribe to user's project/order changes
   * @param {string} userId - User ID
   * @param {Function} onChange - Callback for order changes
   * @returns {Function} - Unsubscribe function
   */
  subscribeToUserOrders(userId, onChange) {
    const channel = supabase?.channel(`user_orders_${userId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          // Convert payload to camelCase
          let convertedData = null;
          if (payload?.new) {
            convertedData = {
              id: payload?.new?.id,
              orderNumber: payload?.new?.order_number,
              projectName: payload?.new?.project_name,
              projectDescription: payload?.new?.project_description,
              projectData: payload?.new?.project_data,
              status: payload?.new?.status,
              paymentStatus: payload?.new?.payment_status,
              subtotal: payload?.new?.subtotal,
              taxAmount: payload?.new?.tax_amount,
              totalAmount: payload?.new?.total_amount,
              currency: payload?.new?.currency,
              paymentIntentId: payload?.new?.payment_intent_id,
              userId: payload?.new?.user_id,
              createdAt: payload?.new?.created_at,
              updatedAt: payload?.new?.updated_at
            };
          }

          onChange({
            eventType: payload?.eventType,
            new: convertedData,
            old: payload?.old
          });
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  /**
   * Subscribe to specific project/order changes
   * @param {string} orderId - Order ID
   * @param {Function} onChange - Callback for order changes
   * @returns {Function} - Unsubscribe function
   */
  subscribeToOrder(orderId, onChange) {
    const channel = supabase?.channel(`order_${orderId}`)?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `id=eq.${orderId}`
        },
        (payload) => {
          const updatedOrder = {
            id: payload?.new?.id,
            orderNumber: payload?.new?.order_number,
            projectName: payload?.new?.project_name,
            projectDescription: payload?.new?.project_description,
            projectData: payload?.new?.project_data,
            status: payload?.new?.status,
            paymentStatus: payload?.new?.payment_status,
            subtotal: payload?.new?.subtotal,
            taxAmount: payload?.new?.tax_amount,
            totalAmount: payload?.new?.total_amount,
            currency: payload?.new?.currency,
            paymentIntentId: payload?.new?.payment_intent_id,
            userId: payload?.new?.user_id,
            createdAt: payload?.new?.created_at,
            updatedAt: payload?.new?.updated_at
          };
          onChange(updatedOrder);
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

function getOrderByPaymentIntent(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getOrderByPaymentIntent is not implemented yet.', args);
  return null;
}

export { getOrderByPaymentIntent };
function getUserSubscription(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getUserSubscription is not implemented yet.', args);
  return null;
}

export { getUserSubscription };
function logOrderConfirmationView(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: logOrderConfirmationView is not implemented yet.', args);
  return null;
}

export { logOrderConfirmationView };